import { CopyOutlined, DeleteOutlined, EllipsisOutlined, EditOutlined } from "@ant-design/icons";
import { Badge, Card, Dropdown, Popconfirm, Tooltip, Typography } from "antd";
import Meta from "antd/lib/card/Meta";
import React, { PropsWithChildren, Suspense } from "react";
import { OutputDesignType } from "~/gql/graphql";
import styles from "./design-thumbnails.module.css";
import { FormValues } from "./create-modal";
import { VPDLabel } from "~/context/taxonomy-context";

type Props = {
  templates: {
    title: string;
    description: string | null;
    id: number;
    html: string;
    active: boolean;
    default: boolean;
    defaultForOrgUnitIds: number[];
    type: OutputDesignType;
    groupItemId: number | null;
  }[];
  handleCardClick?: (id: number) => void;
  onDelete: (id: number) => void;
  onSetDefault: (
    data: {
      data: {
        default: {
          set: boolean;
        };
      };
      id: number;
    }[],
    item: { id: number; default: boolean }
  ) => void;
  onSetActive: (
    data: {
      data: {
        active: {
          set: boolean;
        };
      };
      id: number;
    }[],
    item: { id: number; active: boolean }
  ) => void;
  onEdit: (template: { id: number; formValues: FormValues }) => void;
  onSetPracticeAreaDefaults: (id: number, defaultForOrgUnitIds: number[]) => void;
  clone: (id: number) => void;
};

const RibbonWrapForActive = (
  props: PropsWithChildren<{ active: boolean; default: boolean; hasPracticeAreaDefaults: boolean }>
) => {
  const PA_LABEL = VPDLabel("PracticeArea");

  return (
    <div style={{ position: "relative" }}>
      {props.active && (
        <Badge.Ribbon text="Active" color="green" style={{ zIndex: 1 }}>
          {props.default && (
            <Tooltip
              title={`This will be used as the default template when users generate a proposal, unless it is set a ${PA_LABEL.toLowerCase()} or office locaton level.`}
            >
              <Badge.Ribbon text="Default" style={{ marginTop: 25, zIndex: 2 }}>
                {props.hasPracticeAreaDefaults && (
                  <Badge.Ribbon
                    text={`Default for ${PA_LABEL.toLowerCase()}`}
                    color="purple"
                    style={{ marginTop: 50, zIndex: 3 }}
                  />
                )}
              </Badge.Ribbon>
            </Tooltip>
          )}
          {!props.default && props.hasPracticeAreaDefaults && (
            <Badge.Ribbon
              text={`Default for ${PA_LABEL.toLowerCase()}`}
              color="purple"
              style={{ marginTop: 25, zIndex: 2 }}
            />
          )}
        </Badge.Ribbon>
      )}
      {props.children}
    </div>
  );
};

export const DesignThumbnails: React.FC<Props> = ({
  templates,
  handleCardClick,
  onSetActive,
  onSetDefault,
  onDelete,
  onEdit,
  onSetPracticeAreaDefaults,
  clone,
}) => {
  const PA_LABEL = VPDLabel("PracticeArea", { plural: true });

  if (templates.length === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "75%" }}>
        <Typography.Title level={4}>No designs found.</Typography.Title>
      </div>
    );
  }

  return (
    <div className={styles["emulated-flex-gap"]}>
      {templates.map((item) => {
        return (
          <Suspense key={item.id} fallback={null}>
            <div style={{ height: 300, marginBottom: 40, width: 250 }}>
              <RibbonWrapForActive
                active={item.active}
                default={item.default}
                hasPracticeAreaDefaults={item.defaultForOrgUnitIds.length > 0}
              >
                <Card
                  className={styles["card"]}
                  id={item.id.toString()}
                  actions={[
                    <Dropdown
                      className={styles["dropdown"]}
                      placement="top"
                      key={item.id}
                      menu={{
                        items: [
                          {
                            label: (
                              <Popconfirm
                                title={`Set ${item.active ? "inactive" : "active"}`}
                                onConfirm={() => {
                                  const payload = [
                                    {
                                      data: { active: { set: !item.active } },
                                      id: item.id,
                                    },
                                  ];

                                  // if (item.type === OutputDesignType.Proposal) {
                                  //   const otherActive = templates.filter(
                                  //     (x) => x.active && x.id !== item.id
                                  //   );
                                  //   if (otherActive) {
                                  //     payload.push(
                                  //       ...otherActive.map((x) => ({
                                  //         data: { active: { set: false } },
                                  //         id: x.id,
                                  //       }))
                                  //     );
                                  //   }
                                  // }

                                  onSetActive(payload, item);
                                }}
                                okText={`Set ${item.active ? "inactive" : "active"}`}
                                cancelText="Cancel"
                                placement="right"
                              >
                                <>
                                  <div style={{ height: 40, padding: "0 15 0 15" }}>
                                    {`Set ${item.active ? "inactive" : "active"}`}
                                  </div>
                                  {/* <Divider style={{ margin: 0, padding: 0 }} /> */}
                                </>
                              </Popconfirm>
                            ),
                            onClick: (e) => e.domEvent.stopPropagation(),
                            key: `active-${item.id}`,
                          },
                          {
                            key: `edit-${item.id}`,
                            label: (
                              <div
                                onClick={() =>
                                  item.groupItemId &&
                                  onEdit({
                                    id: item.id,
                                    formValues: {
                                      title: item.title,
                                      description: item.description || "",
                                      groupItemId: item.groupItemId,
                                    },
                                  })
                                } // Trigger onEdit with the template data
                                style={{ height: 40, padding: "0 15 0 15" }}
                              >
                                <span style={{ marginRight: 5 }}>Edit Details</span>
                                <EditOutlined />
                              </div>
                            ),
                            onClick: (e) => e.domEvent.stopPropagation(),
                          },
                          ...(!item.default && item.active
                            ? [
                                {
                                  onClick: (e: { domEvent: { stopPropagation: () => any } }) =>
                                    e.domEvent.stopPropagation(),
                                  label: (
                                    <div
                                      onClick={() => {
                                        onSetDefault(
                                          [
                                            {
                                              data: { default: { set: true } },
                                              id: item.id,
                                            },
                                          ],
                                          item
                                        );
                                      }}
                                      style={{ height: 40, padding: "0 15 0 15" }}
                                    >
                                      Set Default
                                    </div>
                                  ),
                                  key: `default-${item.id}`,
                                },
                              ]
                            : []),
                          ...(item.active
                            ? [
                                {
                                  onClick: (e: { domEvent: { stopPropagation: () => any } }) =>
                                    e.domEvent.stopPropagation(),
                                  label: (
                                    <div
                                      onClick={() => {
                                        onSetPracticeAreaDefaults(
                                          item.id,
                                          item.defaultForOrgUnitIds
                                        );
                                      }}
                                      style={{ height: 40, padding: "0 15 0 15" }}
                                    >
                                      Set Default For {PA_LABEL}
                                    </div>
                                  ),
                                  key: `default-pa-${item.id}`,
                                },
                              ]
                            : []),
                          {
                            key: `clone-${item.id}`,
                            label: (
                              <>
                                <div
                                  onClick={() => clone(item.id)}
                                  style={{ height: 40, padding: "0 15 0 15" }}
                                >
                                  <span style={{ marginRight: 5 }}>Clone</span>
                                  <CopyOutlined />
                                </div>
                                {/* <Divider style={{ margin: 0, padding: 0 }} /> */}
                              </>
                            ),
                            onClick: (e) => e.domEvent.stopPropagation(),
                          },
                          {
                            onClick: (e) => e.domEvent.stopPropagation(),
                            label: (
                              <Popconfirm
                                title="Are you sure you want to delete this template?"
                                onConfirm={() => onDelete(item.id)}
                                okText="Yes"
                                cancelText="No"
                                placement="right"
                              >
                                <div
                                  style={{
                                    height: 30,
                                    color: "red",
                                    paddingTop: 0,
                                    marginRight: 3,
                                    padding: "0 15 0 15",
                                  }}
                                >
                                  Delete <DeleteOutlined />
                                </div>
                              </Popconfirm>
                            ),
                            key: item.id,
                          },
                        ],
                      }}
                      trigger={["click"]}
                    >
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <EllipsisOutlined key="ellipsis" />
                      </div>
                    </Dropdown>,
                  ]}
                  onClick={() => handleCardClick && handleCardClick(item.id)}
                  key={item.id}
                  hoverable
                  cover={
                    <div
                      style={{
                        padding: 30,
                        overflow: "hidden",
                        maxHeight: "100%",
                        height: 200,
                        fontSize: "1px !important",
                        width: 200,
                        position: "relative",
                        zIndex: 0,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: `<div>${item.html}<div>`,
                      }}
                    />
                  }
                >
                  <Meta
                    title={
                      <Tooltip title={item.title} placement="left">
                        <span>{item.title}</span>
                      </Tooltip>
                    }
                    description={
                      <div style={{ overflow: "hidden", height: 40 }}>
                        {item.description || "-"}
                      </div>
                    }
                  />
                </Card>
              </RibbonWrapForActive>
            </div>
          </Suspense>
        );
      })}
    </div>
  );
};
